import { CALENDAR_STATUS_META, isSameDay, type DayInfo } from "@/pages/admin/calendar/calendarUtils";
import { statusMeta } from "@/lib/bookings";

interface WeekViewProps {
  days: DayInfo[];
  selectedIso: string | null;
  onSelect: (iso: string) => void;
}

export default function WeekView({ days, selectedIso, onSelect }: WeekViewProps) {
  const now = new Date();

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-3">
      {days.map((day) => {
        const meta = CALENDAR_STATUS_META[day.status];
        const selected = day.iso === selectedIso;
        const isToday = isSameDay(day.date, now);
        return (
          <button
            key={day.iso}
            type="button"
            onClick={() => onSelect(day.iso)}
            className={`text-left bg-background-50 border rounded-lg p-3 min-h-[220px] transition-colors cursor-pointer ${
              selected ? "border-primary-500 ring-1 ring-primary-500" : "border-background-200 hover:border-background-300"
            }`}
          >
            <div className="flex items-center justify-between gap-2">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wider text-foreground-500">
                  {day.date.toLocaleDateString("en-GB", { weekday: "short" })}
                </p>
                <p
                  className={`font-heading text-xl font-semibold ${
                    isToday ? "text-primary-700" : "text-foreground-950"
                  }`}
                >
                  {day.date.getDate()}
                </p>
              </div>
              <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded-full border ${meta.chip}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`}></span>
                {meta.label}
              </span>
            </div>

            <div className="mt-3 space-y-1.5">
              {day.blocks.length > 0 ? (
                <div className="rounded-md bg-background-100 border border-background-200 px-2.5 py-2">
                  <p className="text-[11px] font-semibold text-foreground-700 flex items-center gap-1">
                    <i className="ri-lock-line"></i>
                    {meta.label}
                  </p>
                  {day.blocks[0].reason ? (
                    <p className="text-[10px] text-foreground-500 mt-0.5 line-clamp-2">{day.blocks[0].reason}</p>
                  ) : null}
                </div>
              ) : null}

              {day.bookings.map((booking) => {
                const bookingMeta = statusMeta(booking.status);
                return (
                  <div
                    key={booking.id}
                    className="rounded-md bg-primary-50 border border-primary-100 px-2.5 py-2"
                  >
                    <p className="text-[11px] font-semibold text-foreground-900 truncate">{booking.name}</p>
                    <p className="text-[10px] text-foreground-500 truncate">{booking.subject || "Enquiry"}</p>
                    <span className={`inline-flex items-center gap-1 text-[9px] font-semibold mt-1 ${bookingMeta.badge} border px-1.5 py-0.5 rounded-full`}>
                      {bookingMeta.label}
                    </span>
                  </div>
                );
              })}

              {day.bookings.length === 0 && day.blocks.length === 0 ? (
                <p className="text-[11px] text-foreground-400 pt-2">No bookings</p>
              ) : null}
            </div>
          </button>
        );
      })}
    </div>
  );
}